import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Router } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import RecoilNexus from 'recoil-nexus';
import Decimal from 'decimal.js';
import { RecoilRoot } from 'recoil';
import * as Sentry from '@sentry/browser';
import ReactGA4 from 'react-ga4';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isToday from 'dayjs/plugin/isToday';
import isBetween from 'dayjs/plugin/isBetween';
import '@/utils/sign-pre';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import '@debank/content-editor/dist/main.css';

import 'antd/es/pagination/style/index.css';
import 'antd/es/message/style/index.css';
import 'antd/es/popover/style/index.css';
import 'antd/es/select/style/index.css';
import 'antd/es/dropdown/style/index.css';
import 'antd/es/slider/style/index.css';
import 'antd/es/radio/style/index.css';
import 'antd/es/checkbox/style/index.css';
import 'antd/es/progress/style/index.css';
import 'antd/es/anchor/style/index.css';
import 'antd/es/auto-complete/style/index.css';
import 'antd/es/input/style/index.css';
import 'antd/es/input-number/style/index.css';
import 'antd/es/date-picker/style/index.css';
import 'antd/es/switch/style/index.css';

import 'css/index.css';
import 'css/antd-index.css';
import 'css/antd-tooltip-modified.css';
import 'react-loading-skeleton/dist/skeleton.css';

import 'utils/theme';
import { IconRcNftBorderClip } from '@/assets/icon/avatar';
import { Censored, RecoilBridgeCollector } from 'components';
import {
  useDeviceTypeIsMobile,
  useCensor,
  useCurrentAddressInfo,
  useSwitch,
  useHiWebSocket,
  useUserType,
} from 'hooks';
import {
  queryClient,
  ENV,
  initHeadUserInfoStr,
  history,
  useForceLogin,
  loadRemoteChains,
} from '@/utils';

import DesktopApp from './DesktopRouter';
import MobileApp from './MobileRouter';
import ScrollToTop from './components/shared/ScrollToTop';
import { securityFeatGA } from './utils/security';
import { useReferralTrack } from './components/ReferralCodeBtn/hooks';
import { isNumber } from 'lodash';

import { RabbykitProvider } from './components/RabbykitProvider';
import { CenterToast } from './components/SystemErrorToast';
import { useAutoTranslateUGC, useLocale } from './utils/i18n';
import { SUPPORTED_LOCALES } from './locales';

Decimal.set({
  precision: 256,
});

dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(relativeTime, {
  thresholds: [
    { l: 's', r: 1 },
    { l: 'm', r: 1 },
    { l: 'mm', r: 59, d: 'minute' },
    { l: 'h', r: 1 },
    { l: 'hh', r: 23, d: 'hour' },
    { l: 'd', r: 1 },
    { l: 'dd', r: 31, d: 'day' },
    { l: 'M', r: 1 },
    { l: 'MM', r: 11, d: 'month' },
    { l: 'y', r: 1 },
    { l: 'yy', d: 'year' },
  ],
});
dayjs.extend(isBetween);

if (!ENV.isTest) {
  Sentry.init({
    dsn: 'https://91efe539bdac4ff291270be3e6b8b372@o460488.ingest.sentry.io/5460820',
    ignoreErrors: [
      // https://github.com/petyosi/react-virtuoso/issues/965
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured with value: hide',
      'OperationError',
      '__puppeteer_evaluation_script__',
      `Failed to execute 'send' on 'WebSocket'`,
      'WebSocket connection failed for host: wss://relay.walletconnect.org',
      /Timeout/i,
      /Loading CSS chunk/i,
      /Loading chunk/i,
      // gtag
      /URI malformed/i,
      /^hide$/,
    ],
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  });
}

const App = () => {
  const isMobile = useDeviceTypeIsMobile();
  const [isCensored, isCensorFetched] = useCensor();
  const { address, isVerified, detail, userInfo } = useCurrentAddressInfo();
  const [currentLocale] = useLocale();
  const { autoTranslateUGC } = useAutoTranslateUGC();
  const userType = useUserType();
  useReferralTrack();

  useHiWebSocket({ isTopApp: true });

  // useLayoutEffect(() => {
  //   // 切换账号后，免得多页面压在 history 里，返回的时候是上一个人的数据
  //   queryClient.removeQueries();
  // }, [address]);

  useEffect(() => {
    if (!address) {
      ReactGA4.gtag('set', 'user_properties', { User_label: 'vistor' });

      ReactGA4.set({ dimension1: 'vistor' });
    } else {
      ReactGA4.gtag('set', 'user_properties', {
        User_label: isVerified ? 'verified' : 'connected',
      });

      ReactGA4.set({ dimension1: isVerified ? 'verified' : 'connected' });
      if (isVerified) {
        ReactGA4.event({ category: 'User Login Status', action: 'User Verified' });
        const userAssets = isNumber((userInfo || detail)?.desc?.usd_value)
          ? (userInfo || detail)?.desc?.usd_value
          : 0;
        if ((userAssets as number) >= 10) {
          ReactGA4.event({ category: 'User Login Status', action: 'User Verified With Assets' });
        }
      }
    }
  }, [address, detail, detail?.desc?.usd_value, isVerified, userInfo]);

  useEffect(() => {
    if (currentLocale) {
      const enAbbr = SUPPORTED_LOCALES.find(x => x.key === currentLocale)?.enAbbr;
      ReactGA4.event({
        category: 'AI Language',
        action: `Using_Language_${enAbbr}_${userType}`,
      });
      if (autoTranslateUGC) {
        ReactGA4.event({
          category: 'AI Language',
          action: `Auto_Translate_Language_${enAbbr}_${userType}`,
        });
      }
    }
  }, [autoTranslateUGC, currentLocale, userType]);

  useEffect(() => {
    if (!!window.ethereum) {
      ReactGA4.event({ category: 'Wallet Injected', action: 'Wallet Injected' });
    }
  }, []);

  useEffect(() => {
    securityFeatGA();
  }, []);

  return isCensorFetched ? (
    isCensored ? (
      <Censored />
    ) : isMobile ? (
      <MobileApp />
    ) : (
      <DesktopApp />
    )
  ) : null;
};

const LoginedApp = () => {
  const { on, turnOn } = useSwitch();

  useForceLogin();
  useLayoutEffect(() => {
    const load = async () => {
      if (!on) {
        try {
          initHeadUserInfoStr();
          await loadRemoteChains();
        } catch (error) {
          (error as any).message = `加载登陆信息失败 ${(error as any)?.message}`;
          Sentry.captureException(error);
        }
        turnOn();
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // console.log('LoginedApp');

  return on ? (
    <SkeletonTheme baseColor="var(--bg-light-color)" highlightColor="var(--bg-default-color)">
      <IconRcNftBorderClip />
      <App />
      <CenterToast />
    </SkeletonTheme>
  ) : null;
};

ReactDOM.render(
  <Router history={history}>
    <QueryClientProvider client={queryClient}>
      <ScrollToTop />
      <RecoilRoot>
        <RecoilNexus />
        <RabbykitProvider>
          <Suspense fallback={null}>
            <LoginedApp />
          </Suspense>
        </RabbykitProvider>
        <RecoilBridgeCollector />
      </RecoilRoot>
    </QueryClientProvider>
  </Router>,
  document.getElementById('root'),
);
